import React, { useState, useEffect, useCallback } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import MobileActionMenu from "../../components/MobileActionMenu";
import ContratoModal from "./ContratoModal";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import SendIcon from "@material-ui/icons/Send";
import LinkIcon from "@material-ui/icons/Link";
import PeopleIcon from "@material-ui/icons/People";
import GetAppIcon from "@material-ui/icons/GetApp";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import CircularProgress from "@material-ui/core/CircularProgress";
import { toast } from "react-toastify";
import { listContratos, deleteContrato, sendContrato, getContrato } from "../../services/contratoService";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: "transparent",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 24px",
    borderBottom: "1px solid #e0e0e0",
    flexWrap: "wrap",
    gap: 12,
    [theme.breakpoints.down("sm")]: { padding: "12px 16px", flexDirection: "column", alignItems: "stretch" },
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 16,
  },
  headerTitle: {
    fontSize: "1.5rem",
    fontWeight: 600,
    color: "#1a1a1a",
    [theme.breakpoints.down("sm")]: { fontSize: "1.2rem" },
  },
  headerSubtitle: {
    fontSize: "0.875rem",
    color: "#666",
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: { flexDirection: "column", alignItems: "stretch" },
  },
  searchField: {
    backgroundColor: "#fff",
    borderRadius: 8,
    minWidth: 220,
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      "& fieldset": { borderColor: "#e0e0e0" },
      "&:hover fieldset": { borderColor: "#1976d2" },
    },
    [theme.breakpoints.down("sm")]: { minWidth: "unset", width: "100%" },
  },
  addButton: {
    borderRadius: 8,
    padding: "6px 20px",
    textTransform: "none",
    fontWeight: 600,
    [theme.breakpoints.down("sm")]: { width: "100%" },
  },
  content: {
    padding: "16px 24px",
    [theme.breakpoints.down("sm")]: { padding: "12px 12px 16px" },
  },
  filterRow: {
    display: "flex",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  activeChip: {
    backgroundColor: "var(--sidebar-color, #1e293b)",
    color: "#fff",
    fontWeight: 600,
    "&:hover": { backgroundColor: "var(--sidebar-color, #1e293b)", opacity: 0.9 },
  },
  tableWrapper: {
    backgroundColor: "#fff",
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  },
  tableScroll: { overflowX: "auto", width: "100%" },
  tableHead: {
    backgroundColor: "var(--sidebar-color, #1e293b)",
    "& th": {
      color: "#cbd5e1",
      fontWeight: 600,
      fontSize: "0.8rem",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      borderBottom: "none",
      padding: "14px 16px",
    },
  },
  tableBody: {
    "& td": {
      padding: "12px 16px",
      fontSize: "0.875rem",
      color: "#334155",
      borderBottom: "1px solid #f1f5f9",
    },
    "& tr:hover": {
      backgroundColor: "#f8fafc",
    },
  },
  actionBtn: {
    minWidth: "auto",
    padding: "4px 8px",
    borderRadius: 6,
    fontWeight: 600,
    fontSize: "0.8rem",
    textTransform: "none",
  },
  paginationBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    borderTop: "1px solid #f1f5f9",
    backgroundColor: "#fff",
    borderRadius: "0 0 12px 12px",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 24px",
    color: "#999",
  },
}));

const STATUS_CONFIG = {
  draft: { label: "Rascunho", style: { backgroundColor: "#90a4ae", color: "#fff" } },
  sent: { label: "Enviado", style: { backgroundColor: "#1565c0", color: "#fff" } },
  partially_signed: { label: "Parc. Assinado", style: { backgroundColor: "#e65100", color: "#fff" } },
  signed: { label: "Assinado", style: { backgroundColor: "#2e7d32", color: "#fff" } },
  cancelled: { label: "Cancelado", style: { backgroundColor: "#c62828", color: "#fff" } },
};

const FILTERS = [
  { value: "", label: "Todos" },
  { value: "draft", label: "Rascunho" },
  { value: "sent", label: "Enviado" },
  { value: "partially_signed", label: "Parc. Assinado" },
  { value: "signed", label: "Assinado" },
  { value: "cancelled", label: "Cancelado" },
];

const ROWS_PER_PAGE = 20;

export default function ContratosPage() {
  const classes = useStyles();
  const theme = useTheme();
  const sidebarColor = theme.palette.primary.main || "#1e293b";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [contratos, setContratos] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [sending, setSending] = useState(null);
  const [modal, setModal] = useState({ open: false, id: null });
  const [signaturesDialog, setSignaturesDialog] = useState({ open: false, contrato: null, loading: false });
  const [downloadingPdf, setDownloadingPdf] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = { page: pageNumber, limit: ROWS_PER_PAGE };
      if (filterStatus) params.status = filterStatus;
      if (searchParam) params.search = searchParam;
      const data = await listContratos(params);
      setContratos(data.contratos || data);
      setTotal(data.total || (data.contratos || data).length);
    } catch {
      toast.error("Erro ao carregar contratos.");
    }
  }, [pageNumber, filterStatus, searchParam]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { setPageNumber(1); }, [filterStatus, searchParam]);

  const handleDelete = async () => {
    try {
      await deleteContrato(deleteDialog.id);
      toast.success("Contrato excluído.");
      setDeleteDialog({ open: false, id: null });
      load();
    } catch {
      toast.error("Erro ao excluir contrato.");
    }
  };

  const handleSend = async (id) => {
    setSending(id);
    try {
      await sendContrato(id);
      toast.success("Contrato enviado para os signatários!");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao enviar.");
    } finally {
      setSending(null);
    }
  };

  const handleCopyLink = (slug) => {
    const url = `${window.location.origin}/assinar/${slug}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Link copiado!"));
  };

  const handleViewSignatures = async (id) => {
    setSignaturesDialog({ open: true, contrato: null, loading: true });
    try {
      const data = await getContrato(id);
      setSignaturesDialog({ open: true, contrato: data, loading: false });
    } catch {
      toast.error("Erro ao carregar detalhes do contrato.");
      setSignaturesDialog({ open: false, contrato: null, loading: false });
    }
  };

  const handleDownloadPDF = async (id) => {
    setDownloadingPdf(id);
    try {
      const c = await getContrato(id);
      const signaturesHtml = (c.signers || []).map((s) => `
        <div style="border:1px solid #ddd;border-radius:8px;padding:16px;margin-bottom:12px;display:flex;align-items:flex-start;gap:16px;">
          <div style="flex:1;">
            <strong>${s.name || "—"}</strong>${s.role ? ` <span style="color:#666;font-size:13px;">— ${s.role}</span>` : ""}
            <br/><span style="color:#666;font-size:13px;">${s.email || ""}</span>
            ${s.signed
              ? `<br/><span style="color:#2e7d32;font-size:12px;">✓ Assinou em ${new Date(s.signedAt).toLocaleString("pt-BR")}</span>`
              : `<br/><span style="color:#e65100;font-size:12px;">⏳ Pendente</span>`}
          </div>
          ${s.signatureData ? `<img src="${s.signatureData}" style="max-height:80px;max-width:200px;border:1px solid #eee;border-radius:4px;background:#fff;" alt="Assinatura"/>` : ""}
        </div>
      `).join("");

      const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8"/>
        <title>${c.title}</title>
        <style>
          body{font-family:Arial,sans-serif;max-width:800px;margin:0 auto;padding:32px;color:#222;}
          h1{font-size:22px;margin-bottom:4px;}
          .header{background:${c.headerColor||"#1e293b"};color:#fff;padding:20px;border-radius:8px;margin-bottom:24px;}
          .section{margin-bottom:24px;}
          .clause-title{font-weight:bold;margin-top:16px;margin-bottom:4px;}
          ${c.cssStyle||""}
          @media print{button{display:none!important;}}
        </style>
      </head><body>
        <div class="header">
          ${c.logoUrl ? `<img src="${c.logoUrl.startsWith("/public") ? window.location.origin.replace("3000","8080")+c.logoUrl : c.logoUrl}" style="max-height:60px;margin-bottom:8px;display:block;"/>` : ""}
          <h1>${c.title}</h1>
          ${c.dueDate ? `<div style="opacity:.8;font-size:13px;">Vencimento: ${new Date(c.dueDate+"T00:00:00").toLocaleDateString("pt-BR")}</div>` : ""}
        </div>
        <div class="section">${c.htmlContent||""}</div>
        ${(c.clauses||[]).map(cl=>`<div class="section"><div class="clause-title">${cl.title}</div><div style="white-space:pre-wrap;font-size:14px;">${cl.content}</div></div>`).join("")}
        <hr style="margin:32px 0;"/>
        <h2 style="font-size:16px;margin-bottom:16px;">Signatários</h2>
        ${signaturesHtml}
        <p style="font-size:11px;color:#999;margin-top:32px;text-align:center;">
          Documento gerado em ${new Date().toLocaleString("pt-BR")}
        </p>
      </body></html>`;

      const win = window.open("", "_blank");
      if (win) {
        win.document.write(html);
        win.document.close();
        win.focus();
        setTimeout(() => win.print(), 600);
      }
    } catch {
      toast.error("Erro ao gerar PDF.");
    } finally {
      setDownloadingPdf(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / ROWS_PER_PAGE));
  const hasMore = pageNumber < totalPages;

  return (
    <Box className={classes.root} style={{ "--sidebar-color": sidebarColor }}>
      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          <Box>
            <Typography className={classes.headerTitle}>Contratos</Typography>
            <Typography className={classes.headerSubtitle}>
              {total} contrato(s) encontrado(s)
            </Typography>
          </Box>
        </Box>

        <Box className={classes.headerRight}>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Buscar contratos..."
            value={searchParam}
            onChange={(e) => { const v = e.target.value; setSearchParam(v); }}
            className={classes.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "#999" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            className={classes.addButton}
            startIcon={<AddIcon />}
            onClick={() => setModal({ open: true, id: null })}
          >
            Novo Contrato
          </Button>
        </Box>
      </Box>

      {/* Conteúdo */}
      <Box className={classes.content}>
        {/* Filtros de status */}
        <Box className={classes.filterRow}>
          {FILTERS.map((f) => (
            <Chip
              key={f.value}
              label={f.label}
              clickable
              className={filterStatus === f.value ? classes.activeChip : ""}
              onClick={() => setFilterStatus(f.value)}
            />
          ))}
        </Box>

        {/* Tabela */}
        <Box className={classes.tableWrapper}>
          <Box className={classes.tableScroll}>
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>Título</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Vencimento</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody className={classes.tableBody}>
              {contratos.map((c) => {
                const st = STATUS_CONFIG[c.status] || { label: c.status, style: {} };
                return (
                  <TableRow key={c.id}>
                    <TableCell>
                      <Typography variant="body2" style={{ fontWeight: 500 }}>
                        {c.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {c.contact && c.contact.name ? c.contact.name : "—"}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={st.label} style={st.style} />
                    </TableCell>
                    <TableCell>
                      {c.dueDate
                        ? new Date(c.dueDate + "T00:00:00").toLocaleDateString("pt-BR")
                        : "—"}
                    </TableCell>
                    <TableCell align="right">
                      {isMobile ? (
                        <MobileActionMenu actions={[
                          { label: "Ver assinaturas", icon: <PeopleIcon fontSize="small" />, onClick: () => handleViewSignatures(c.id) },
                          { label: "Baixar PDF", icon: <GetAppIcon fontSize="small" />, onClick: () => handleDownloadPDF(c.id), disabled: downloadingPdf === c.id },
                          { label: "Editar", icon: <EditIcon fontSize="small" />, onClick: () => setModal({ open: true, id: c.id }) },
                          { label: "Enviar para assinatura", icon: <SendIcon fontSize="small" />, onClick: () => handleSend(c.id), disabled: c.status !== "draft" || sending === c.id },
                          { label: "Copiar link", icon: <LinkIcon fontSize="small" />, onClick: () => handleCopyLink(c.slug) },
                          { label: "Excluir", icon: <DeleteOutlineIcon fontSize="small" />, onClick: () => setDeleteDialog({ open: true, id: c.id }), color: "#ef4444" },
                        ]} />
                      ) : (
                        <Box display="flex" justifyContent="flex-end" style={{ gap: 2 }}>
                          <Tooltip title="Ver assinaturas">
                            <IconButton size="small" className={classes.actionBtn} onClick={() => handleViewSignatures(c.id)}>
                              <PeopleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Baixar PDF">
                            <IconButton size="small" className={classes.actionBtn} onClick={() => handleDownloadPDF(c.id)} disabled={downloadingPdf === c.id}>
                              {downloadingPdf === c.id ? <CircularProgress size={14} /> : <GetAppIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar">
                            <IconButton size="small" className={classes.actionBtn} onClick={() => setModal({ open: true, id: c.id })}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={c.status !== "draft" ? "Apenas rascunhos podem ser enviados" : "Enviar para assinatura"}>
                            <span>
                              <IconButton size="small" className={classes.actionBtn} disabled={c.status !== "draft" || sending === c.id} onClick={() => handleSend(c.id)}>
                                <SendIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Copiar link de assinatura">
                            <IconButton size="small" className={classes.actionBtn} onClick={() => handleCopyLink(c.slug)}>
                              <LinkIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir">
                            <IconButton size="small" className={classes.actionBtn} onClick={() => setDeleteDialog({ open: true, id: c.id })}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}

              {contratos.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box className={classes.emptyState}>
                      <Typography variant="body2">Nenhum contrato encontrado.</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </Box>

          {/* Paginação */}
          {total > 0 && (
            <Box className={classes.paginationBar}>
              <Typography variant="body2" style={{ color: "#666" }}>
                Exibindo {(pageNumber - 1) * ROWS_PER_PAGE + 1} a{" "}
                {Math.min(pageNumber * ROWS_PER_PAGE, total)} de {total} resultado(s)
              </Typography>
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <Button
                  size="small"
                  disabled={pageNumber === 1}
                  onClick={() => setPageNumber((p) => p - 1)}
                  className={classes.actionBtn}
                >
                  Anterior
                </Button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pg = i + 1;
                  if (totalPages > 5) {
                    const start = Math.max(1, Math.min(pageNumber - 2, totalPages - 4));
                    pg = start + i;
                  }
                  return (
                    <Button
                      key={pg}
                      size="small"
                      variant={pg === pageNumber ? "contained" : "text"}
                      color={pg === pageNumber ? "primary" : "default"}
                      onClick={() => setPageNumber(pg)}
                      style={{
                        minWidth: 32,
                        borderRadius: 6,
                        fontWeight: pg === pageNumber ? 700 : 400,
                      }}
                    >
                      {pg}
                    </Button>
                  );
                })}
                <Button
                  size="small"
                  disabled={!hasMore}
                  onClick={() => setPageNumber((p) => p + 1)}
                  className={classes.actionBtn}
                >
                  Próximo
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <ContratoModal
        open={modal.open}
        contratoId={modal.id}
        onClose={(reload) => {
          setModal({ open: false, id: null });
          if (reload) load();
        }}
      />

      {/* Dialog de assinaturas */}
      <Dialog
        open={signaturesDialog.open}
        onClose={() => setSignaturesDialog({ open: false, contrato: null, loading: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {signaturesDialog.contrato ? signaturesDialog.contrato.title : "Assinaturas"}
        </DialogTitle>
        <DialogContent>
          {signaturesDialog.loading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : signaturesDialog.contrato ? (
            (signaturesDialog.contrato.signers || []).length === 0 ? (
              <Typography color="textSecondary">Nenhum signatário cadastrado.</Typography>
            ) : (
              (signaturesDialog.contrato.signers || []).map((s, idx) => (
                <Box
                  key={s.id || idx}
                  display="flex"
                  alignItems="center"
                  style={{ gap: 12, padding: "10px 0", borderBottom: "1px solid #f0f0f0" }}
                >
                  {s.signed
                    ? <CheckCircleIcon style={{ color: "#2e7d32", flexShrink: 0 }} />
                    : <HourglassEmptyIcon style={{ color: "#e65100", flexShrink: 0 }} />}
                  <Box flexGrow={1}>
                    <Typography variant="body2">
                      <strong>{s.name}</strong>
                      {s.role ? ` — ${s.role}` : ""}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {s.email}
                    </Typography>
                    {s.signed && s.signedAt && (
                      <Typography variant="caption" display="block" style={{ color: "#2e7d32" }}>
                        Assinou em {new Date(s.signedAt).toLocaleString("pt-BR")}
                      </Typography>
                    )}
                    {!s.signed && (
                      <Typography variant="caption" display="block" style={{ color: "#e65100" }}>
                        Pendente
                      </Typography>
                    )}
                  </Box>
                  {s.signatureData && (
                    <img
                      src={s.signatureData}
                      alt="Assinatura"
                      style={{ maxHeight: 50, maxWidth: 120, border: "1px solid #eee", borderRadius: 4, background: "#fff" }}
                    />
                  )}
                </Box>
              ))
            )
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSignaturesDialog({ open: false, contrato: null, loading: false })}>
            Fechar
          </Button>
          {signaturesDialog.contrato && (
            <Button
              color="primary"
              onClick={() => {
                handleDownloadPDF(signaturesDialog.contrato.id);
                setSignaturesDialog({ open: false, contrato: null, loading: false });
              }}
            >
              Baixar PDF
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog de exclusão */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
      >
        <DialogTitle>Excluir Contrato</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Esta ação não pode ser desfeita. Confirma a exclusão do contrato?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>
            Cancelar
          </Button>
          <Button color="secondary" onClick={handleDelete}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
